import React, { useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { DownloadIcon, TrashIcon } from 'components/icons/Icons';
import { Button } from 'components/ui/Button';
import { SupportingFile } from 'shared/types/optim/supportingFiles';
import { downloadDocumentByStoredId } from 'services/documentsAPI';
import { FileTypeEnum } from 'api/documents';
import { GenericLoading } from 'components/ui/Loader';
import { Sizes } from 'components/domain/ProfilingForm/RadioButtonChoice';
import { actorsAPI } from 'services/swagger-api';
import { GenericDocumentsManager } from 'components/domain/Document/GenericDocumentManager';
import { DocumentUploaderConfig } from 'types/form/componentConfig';
import { useQuoteContractualizationData } from 'features/subscription-process/contractualization-process/store';

interface FormikDocumentUploaderProps extends Omit<DocumentUploaderConfig, 'type' | 'componentKey'> {
  disabled?: boolean
}

export const FormikDocumentUploader: React.FC<FormikDocumentUploaderProps> = ({
  name,
  label,
  sectionLabel,
  successMessage,
  fileType,
  required = false,
  disabled = false,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
  customDownloadFileName
}) => {
  const [field, meta] = useField(name);
  const { setFieldValue, values } = useFormikContext();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const quoteData = useQuoteContractualizationData();

  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);

  const handleUpload = async (file: File, fileType: FileTypeEnum): Promise<void> => {
    try {
      setFieldValue(name, {
        ...((values as any)[name] as SupportingFile),
        file
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemove = async (storedFileId: string) => {
    try {
      if (storedFileId === 'temp-file-id') {
        setFieldValue(name, undefined);
      } else {
        if (quoteData?.insured.id)
          switch (fileType) {
            case FileTypeEnum.PieceIdentite:
              const response = await actorsAPI.deletePersonDocument({ personId: quoteData.insured.id, storedFileId })
              if (response.isSuccess) {
                setFieldValue(name, undefined);
              }
          }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async () => {
    const value = field.value;
    if (!value) return;

    try {
      setIsDownloading(true);
      let blob: Blob | null = null;

      if (value.storedFileId) {
        blob = await downloadDocumentByStoredId(value.storedFileId);
      } else if (value.file instanceof File) {
        blob = value.file;
      } else if (value instanceof File || value instanceof Blob) {
        blob = value;
      }

      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = customDownloadFileName || value.name || value.fileName || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error during download:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getStoredFileId = () => {
    const value = field.value;
    console.log(value)
    if (value?.storedFileId) return value.storedFileId;
    if (value?.file) return 'temp-file-id';
    return undefined;
  };

  const renderUploadedActions = (storedFileId: string, onRemove?: () => void) => (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        color='slate'
        onClick={handleDownload}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <GenericLoading size={Sizes.sm} isInline />
        ) : (
          <DownloadIcon />
        )}
      </Button>
      {onRemove && (
        <Button
          type="button"
          color='slate'
          onClick={onRemove}
          disabled={disabled}
        >
          <TrashIcon />
        </Button>
      )}
    </div>
  );

  const hasFile = getStoredFileId() !== undefined;

  return <GenericDocumentsManager
    renderUploadedActions={renderUploadedActions}
    title={t(sectionLabel || '')}
    completedSectionLabel={t(successMessage || '')}
    onUpload={(sectionId, file, fileType) => handleUpload(file, fileType)}
    onRemove={(sectionId, fileId) => handleRemove(fileId)}
    disabled={disabled}
    acceptedFormats={accept}
    sections={[{
      id: name,
      title: t(label),
      isCompleted: hasFile,
      documents: [
        {
          fileType,
          name: t(label),
          storedFileId: getStoredFileId()
        }
      ]
    }]}
  />
};
